/**
 * AddInsServiceConfiguration Component
 * 
 * This component manages the configuration of add-in services for enterprise groups.
 * It allows users to:
 * - Configure add-in credentials (agency code, username, password)
 * - Associate subscribers to add-ins
 * - View subscription details
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Breadcrumbs,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Link,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import ApiServices from "../../services/ApiServices";
import {
    ADDIN_CONFIGURATION,
    ADDIN_CONFIGURATIONADD,
    ADDIN_CONFIGURATIONEDIT,
    ADDIN_CONFIGURATIONDELETE,
    ADDIN_CONFIG_ALL_SUBS,
    ADDIN_CONFIG_ALL_SUBSUPDATE,
} from "../../constants/api-constants";
import Confirmation from "../../common/Confirmation/Confirmation";
import { usePageMode } from "../../hooks/usePageMode";
import { useLoading } from "../../hooks/useLoader";
import "./AddInsServiceConfiguration.css";
import AddInsManageSubscriber from "./AddInsManageSubscriber/AddInsManageSubscriber";
import ManageSubscriptiondetails from "./ManageSubscriptiondetails";

// ============================================================================
// CONSTANTS
// ============================================================================

/** Masked password value displayed in the UI */
const MASKED_VALUE = "*".repeat(8);

/** Ordered list of main accordion section keys used for Update & Next navigation */
const SECTION_ORDER = ["configure", "subscribers", "subscription"];

// ============================================================================
// HELPER FUNCTIONS - Data Initialization & Transformation
// ============================================================================

/**
 * Creates an empty initial state for the component
 * @returns {Object} Empty state object with all required properties
 */
const createEmptyState = () => ({
    pageSummary: {
        addInName: "",
        vendor: "",
        addInTypeText: "",
        costTypeText: "",
        subscriptionStatus: "",
        includedAddIns: [],
    },
    addIns: [],
    subscribers: [],
    subscriptionDetails: {
        planName: "",
        renewalDate: "",
        seats: "",
    },
});

/**
 * Converts a value to an array, handling various input types
 * @param {*} value - Value to convert to array
 * @returns {Array} Array representation of the value
 */
const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
};

const getSubscribersFromResponse = (response) => {
    const result = response?.result;
    return Array.isArray(result)
        ? result
        : toArray(
            result?.associatedSubscribers ??
            result?.subscribers ??
            result?.records ??
            result?.items,
        );
};

const getSubscriberId = (subscriber) =>
    Number(
        subscriber?.subscriberId ??
        subscriber?.id ??
        subscriber?.distributorUserId ??
        subscriber?.userId ??
        subscriber?.subscriberUserId ??
        0,
    );

const getSubscriberAccess = (subscriber) => {
    if (typeof subscriber?.hasAccess === "boolean") return subscriber.hasAccess;
    if (typeof subscriber?.isAssociated === "boolean") return subscriber.isAssociated;
    if (typeof subscriber?.isSelected === "boolean") return subscriber.isSelected;
    return true;
};

const getFirstSubscriberAccess = (response) => {
    const subscriber = getSubscribersFromResponse(response).find(
        (item) => getSubscriberId(item) > 0,
    );

    if (!subscriber) return null;

    return {
        subscriberId: getSubscriberId(subscriber),
        hasAccess: getSubscriberAccess(subscriber),
    };
};

const getSubscriberAssignmentMode = (response) => {
    const result = response?.result;
    const subscribers = getSubscribersFromResponse(response);

    return Number(
        result?.assignmentMode ??
        result?.subscriberAssignmentMode ??
        response?.assignmentMode ??
        response?.subscriberAssignmentMode ??
        subscribers.find((subscriber) => subscriber?.assignmentMode != null)?.assignmentMode ??
        0,
    );
};

const buildSubscriberAssignmentPayload = (enterpriseId, addInsId) => ({
    isShowChildUserData: true,
    searchText: "",
    SearchText: "",
    selectedIds: [],
    pageSize: 1,
    PageSize: 1,
    pageNumber: 1,
    PageNumber: 1,
    ignorePaging: false,
    sortColumn: "",
    sortType: "",
    filter: {
        enterpriseGroupId: enterpriseId,
        addInId: addInsId,
        searchText: "",
        SearchText: "",
    },
    isSearchInAllQuery: true,
});

/**
 * Maps a configuration item from API response to normalized format
 * Handles multiple possible property names from different API versions
 * @param {Object} cfg - Configuration object from API
 * @param {number} index - Index for fallback ID generation
 * @returns {Object} Normalized configuration object
 */
const mapConfigItem = (cfg, index) => {
    // Extract field values from integrationTypeAPIKey array
    const fields = {};
    if (cfg?.integrationTypeAPIKey && Array.isArray(cfg.integrationTypeAPIKey)) {
        cfg.integrationTypeAPIKey.forEach((field) => {
            fields[field.keyName] = field.keyValue || "";
        });
    }

    return {
        id: cfg?.userAPIKeyId ?? cfg?.id ?? cfg?.configurationId ?? cfg?.addInConfigurationId ?? index + 1,
        configurationName: cfg?.name ?? cfg?.configurationName ?? "",
        fields,
        isValidCredentials: cfg?.isValidCredentials ?? true,
        error: cfg?.error || null,
        // Legacy fields for backward compatibility
        agencyCode: cfg?.agencyCode ?? cfg?.agencyName ?? cfg?.agencyNumber ?? (fields.AgencyCode || fields.AgencyName || ""),
        username: cfg?.username ?? cfg?.userName ?? (fields.UserName || fields.Username || ""),
        passwordMasked: cfg?.passwordMasked || MASKED_VALUE,
    };
};

/**
 * Maps an add-in item from API response to normalized format
 * @param {Object} item - Add-in object from API
 * @param {number} fallbackAddInId - Fallback ID if none exists in the item
 * @returns {Object} Normalized add-in object with configurations
 */
const mapAddInItem = (item, fallbackAddInId) => {
    // Extract and normalize configurations from various possible property names
    const configurations = toArray(
        item?.configurations ?? item?.addInConfigurations ?? item?.integrationConfigurations,
    ).map((cfg, index) => mapConfigItem(cfg, index));

    // Extract configuration template/fields
    const configTemplate = item?.configurationTemplate || {};
    const configFields = toArray(configTemplate?.configurationFields || []);

    // Enrich each field with auto-fill defaultValue and readOnly flag
    const enrichedConfigFields = configFields.map((field) => ({
        ...field,
        defaultValue: field.defaultKeyValue ?? field.defaultValue ?? null,
        readOnly: !!(field.enterKeyValue === true && (field.defaultKeyValue ?? field.defaultValue)),
    }));

    return {
        id: item?.id ?? item?.addInId ?? item?.integrationSolutionId ?? fallbackAddInId,
        name: item?.name ?? item?.addInName ?? item?.integrationSolutionName ?? "",
        integrationSolutionName: item?.integrationSolutionName ?? item?.name ?? item?.addInName ?? "",
        configLabel: item?.configLabel ?? "Internal Configuration",
        requiresConfiguration:
            item?.requiresConfiguration ?? item?.isConfigurationRequired ?? true,
        allowMultipleConfigurations:
            item?.allowMultipleConfigurations ?? item?.isMultipleConfiguration ?? true,
        isConfigured: item?.isConfigured ?? (configurations.length > 0),
        configurations,
        configurationFields: enrichedConfigFields,
        noConfigMessage:
            item?.noConfigMessage ??
            "This integration does not require configuration.",
    };
};

/**
 * Maps the complete API result to the component's state structure
 * Handles multiple API response formats and provides fallback values
 * @param {Object} result - API response result object
 * @param {Object} fallbackState - Fallback state to use if data is missing
 * @param {number} addInsId - Add-in ID from URL params
 * @returns {Object} Complete normalized state object
 */
const mapApiResultToState = (result, fallbackState, addInsId) => {
    let normalizedAddIns = [];

    // Check if result has an array of add-ins
    const addInsSource = toArray(
        result?.addIns ?? result?.addinServices ?? result?.records ?? result?.addIn,
    );

    if (addInsSource.length) {
        // Handle array of add-ins
        normalizedAddIns = addInsSource.map((item) => mapAddInItem(item, addInsId));
    } else if (result?.addInId || result?.integrationSolutionId) {
        // Handle single add-in object (result itself is the add-in)
        normalizedAddIns = [mapAddInItem(result, addInsId)];
    }

    // Build page summary with fallback values for all fields
    return {
        pageSummary: {
            addInName:
                result?.pageSummary?.addInName ??
                result?.addInName ??
                normalizedAddIns[0]?.name ??
                fallbackState.pageSummary.addInName,
            vendor:
                result?.pageSummary?.vendor ??
                result?.vendor ??
                result?.vendorName ??
                fallbackState.pageSummary.vendor,
            addInTypeText:
                result?.pageSummary?.addInTypeTextText ??
                result?.addInTypeTextText ??
                result?.pageSummary?.addInTypeText ??
                result?.addInTypeText ??
                fallbackState.pageSummary.addInTypeText,
            costTypeText:
                result?.pageSummary?.costTypeTextText ??
                result?.costTypeTextText ??
                result?.pageSummary?.costTypeText ??
                result?.costTypeText ??
                fallbackState.pageSummary.costTypeText,
            subscriptionStatus:
                result?.pageSummary?.subscriptionStatusText ??
                result?.subscriptionStatusText ??
                result?.pageSummary?.subscriptionStatus ??
                result?.subscriptionStatus ??
                fallbackState.pageSummary.subscriptionStatus,
            includedAddIns: (() => {
                const apiIncluded = toArray(result?.pageSummary?.includedAddIns ?? result?.includedAddIns);
                if (apiIncluded.length) return apiIncluded;
                const configNames = normalizedAddIns
                    .flatMap((item) => item.configurations.map((cfg) => cfg.configurationName))
                    .filter(Boolean);
                if (configNames.length) return configNames;
                return normalizedAddIns.map((item) => item.name).filter(Boolean);
            })(),
        },
        addIns: normalizedAddIns,
        subscribers: toArray(result?.subscribers),
        subscriptionDetails: result?.subscriptionDetails ?? fallbackState.subscriptionDetails,
    };
};

// ============================================================================
// HELPER FUNCTIONS - Configuration Management
// ============================================================================

/**
 * Builds updated configurations array when adding or editing a configuration
 * @param {Object} params - Configuration parameters
 * @param {Array} params.currentConfigurations - Existing configurations array
 * @param {number|null} params.configId - Config ID (null for new config)
 * @param {string} params.configurationName - Configuration name
 * @param {Object} params.fields - Dynamic field values
 * @returns {Array} Updated configurations array
 */
const buildUpdatedConfigurations = ({
    currentConfigurations,
    configId,
    configurationName,
    fields,
}) => {
    // If editing existing configuration
    if (configId) {
        return currentConfigurations.map((cfg) => {
            if (cfg.id !== configId) return cfg;

            return {
                ...cfg,
                configurationName,
                fields: { ...cfg.fields, ...fields },
                // Update legacy fields for backward compatibility
                agencyCode: fields.AgencyCode || fields.AgencyName || cfg.agencyCode,
                username: fields.UserName || fields.Username || cfg.username,
            };
        });
    }

    // If adding new configuration, generate next ID
    const nextId = currentConfigurations.length
        ? Math.max(...currentConfigurations.map((cfg) => cfg.id)) + 1
        : 1;

    return [
        ...currentConfigurations,
        {
            id: nextId,
            configurationName,
            fields,
            isValidCredentials: true,
            error: null,
            // Legacy fields for backward compatibility
            agencyCode: fields.AgencyCode || fields.AgencyName || "",
            username: fields.UserName || fields.Username || "",
            passwordMasked: MASKED_VALUE,
        },
    ];
};

/**
 * Updates or inserts a configuration for a specific add-in
 * @param {Object} params - Upsert parameters
 * @param {Array} params.addIns - All add-ins array
 * @param {number} params.addInId - Target add-in ID
 * @param {number|null} params.configId - Config ID (null for new)
 * @param {string} params.configurationName - Configuration name
 * @param {Object} params.fields - Dynamic field values
 * @returns {Array} Updated add-ins array
 */
const upsertAddInConfiguration = ({
    addIns,
    addInId,
    configId,
    configurationName,
    fields,
}) => {
    return addIns.map((addIn) => {
        if (addIn.id !== addInId) return addIn;

        return {
            ...addIn,
            configurations: buildUpdatedConfigurations({
                currentConfigurations: addIn.configurations,
                configId,
                configurationName,
                fields,
            }),
        };
    });
};

/**
 * Removes a configuration from a specific add-in
 * @param {Object} params - Delete parameters
 * @param {Array} params.addIns - All add-ins array
 * @param {number} params.addInId - Target add-in ID
 * @param {number} params.configId - Configuration ID to remove
 * @returns {Array} Updated add-ins array
 */
const removeAddInConfiguration = ({ addIns, addInId, configId }) => {
    return addIns.map((addIn) => {
        if (addIn.id !== addInId) return addIn;

        return {
            ...addIn,
            configurations: addIn.configurations.filter((cfg) => cfg.id !== configId),
        };
    });
};

/**
 * Builds expanded add-in IDs set from mapped state
 * @param {Object} mappedState - Normalized component state
 * @returns {Set<number>} Set of add-in IDs
 */
const buildExpandedAddInIds = (mappedState) =>
    new Set(mappedState.addIns.map((item) => item.id));

/**
 * Builds expanded configuration IDs set from mapped state
 * @param {Object} mappedState - Normalized component state
 * @returns {Set<number>} Set of configuration IDs
 */
const buildExpandedConfigIds = (mappedState) => {
    const configIds = [];
    mappedState.addIns.forEach((addIn) => {
        addIn.configurations.forEach((cfg) => {
            configIds.push(cfg.id);
        });
    });
    return new Set(configIds);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AddInsServiceConfiguration = () => {
    // ========================================================================
    // Hooks & Context
    // ========================================================================
    const history = useHistory();
    const location = useLocation();
    const page = usePageMode();
    const { showLoader } = useLoading();

    // ========================================================================
    // URL Parameters
    // ========================================================================

    /** Extract enterprise ID and add-in ID from URL params or location state */
    const queryParams = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const stateEnterpriseId = Number(location.state?.enterpriseId);
        const stateAddInsId = Number(location.state?.addInsId);
        const queryEnterpriseId = Number(searchParams.get("enterpriseId"));
        const queryAddInsId = Number(searchParams.get("addInsId"));

        return {
            enterpriseId: stateEnterpriseId || queryEnterpriseId || 0,
            addInsId: stateAddInsId || queryAddInsId || 0,
        };
    }, [location.search, location.state]);

    // ========================================================================
    // Component State
    // ========================================================================

    /** Main data state containing page summary, add-ins, subscribers, and subscription details */
    const [data, setData] = useState(() => createEmptyState());

    /** Currently open accordion section (configure, subscribers, or subscription) */
    const [openSection, setOpenSection] = useState("configure");

    /** Set of expanded add-in IDs within the Configure Add-ins accordion */
    const [openAddIns, setOpenAddIns] = useState(() => new Set());

    /** Set of expanded configuration IDs within each add-in accordion */
    const [openConfigs, setOpenConfigs] = useState(() => new Set());

    /** Subscriber assignment mode: 'all' or 'selected' */
    const [subscriberAssignment, setSubscriberAssignment] = useState("");

    /** Tracks whether the subscriber assignment radio default has loaded */
    const [isSubscriberAssignmentLoaded, setIsSubscriberAssignmentLoaded] = useState(false);

    /** Selected subscriber IDs reported by AddInsManageSubscriber */
    const [selectedSubscriberIds, setSelectedSubscriberIds] = useState([]);

    /** Signals AddInsManageSubscriber to reload its table data */
    const [subscriberTableRefreshKey, setSubscriberTableRefreshKey] = useState(0);

    /** Configuration editor dialog open state */
    const [editorOpen, setEditorOpen] = useState(false);

    /** State for the configuration editor form */
    const [editorState, setEditorState] = useState({
        addInId: null,
        configId: null,
        configurationName: "",
        fields: {},
    });

    /** State for delete confirmation dialog */
    const [deleteState, setDeleteState] = useState({
        open: false,
        addInId: null,
        configId: null,
    });


    // ========================================================================
    // Data Loading Effect
    // ========================================================================

    /**
     * Loads configuration data from the API when component mounts or params change
     * Fetches add-in configuration including credentials, subscribers, and subscription details
     */
    const loadConfigurationData = useCallback(async () => {
        const { enterpriseId, addInsId } = queryParams;

        // Validate required parameters
        if (!enterpriseId || !addInsId) {
            toast.error("Missing enterprise or add-in ID.");
            return;
        }

        try {
            showLoader(true);

            // Get user role from localStorage for authorization
            const selectedRole = Number(
                localStorage.getItem("selectedRole") ||
                localStorage.getItem("roleId") ||
                0,
            );

            if (selectedRole <= 0) {
                throw new Error("Invalid user role. Please log in again.");
            }

            // Fetch configuration data from API
            const endpoint = `${ADDIN_CONFIGURATION(enterpriseId, addInsId)}?Role=${selectedRole}`;
            const response = await ApiServices.httpGet(endpoint);

            if (!response?.responseStatus?.isSuccess) {
                throw new Error(
                    response?.responseStatus?.error ||
                    "Failed to load add-ins configuration.",
                );
            }

            // Map API response to component state
            const mappedState = mapApiResultToState(
                response?.result || {},
                createEmptyState(),
                addInsId,
            );

            setData(mappedState);
            setOpenAddIns(buildExpandedAddInIds(mappedState));
            setOpenConfigs(buildExpandedConfigIds(mappedState));
        } catch (error) {
            toast.error(error?.message || "Unable to load add-ins configuration.");
        } finally {
            showLoader(false);
        }
    }, [queryParams, showLoader]);

    useEffect(() => {
        loadConfigurationData();
    }, [loadConfigurationData]);

    const loadSubscriberAssignment = useCallback(async () => {
        const enterpriseId = queryParams.enterpriseId;
        const addInsId = queryParams.addInsId;

        if (!enterpriseId || !addInsId) {
            setIsSubscriberAssignmentLoaded(true);
            return;
        }

        setIsSubscriberAssignmentLoaded(false);
        try {
            const response = await ApiServices.httpPost(
                ADDIN_CONFIG_ALL_SUBS(enterpriseId, addInsId),
                buildSubscriberAssignmentPayload(enterpriseId, addInsId),
            );

            if (!response?.responseStatus?.isSuccess) {
                throw new Error(
                    response?.responseStatus?.error || "Failed to load subscriber assignment.",
                );
            }

            setSubscriberAssignment(getSubscriberAssignmentMode(response) === 2 ? "selected" : "all");
        } catch (error) {
            toast.error(error?.message || "Failed to load subscriber assignment.");
            setSubscriberAssignment("all");
        } finally {
            setIsSubscriberAssignmentLoaded(true);
        }
    }, [queryParams.enterpriseId, queryParams.addInsId]);

    useEffect(() => {
        loadSubscriberAssignment();
    }, [loadSubscriberAssignment]);

    // ========================================================================
    // Memoized Values
    // ========================================================================

    /** Summary cards data for the page header */
    const summaryCards = useMemo(
        () => [
            { label: "Add-in Name", value: data.pageSummary.addInName },
            { label: "Vendor", value: data.pageSummary.vendor },
            { label: "Add-in Type", value: data.pageSummary.addInTypeText },
            { label: "Cost Type", value: data.pageSummary.costTypeText },
            {
                label: "Subscription Status",
                value: data.pageSummary.subscriptionStatus,
            },
        ],
        [data.pageSummary],
    );

    // ========================================================================
    // Event Handlers - Accordion Controls
    // ========================================================================

    /** Toggles main section accordions (configure, subscribers, subscription) */
    const toggleSection = (section) => (_, expanded) => {
        setOpenSection(expanded ? section : "");
    };

    /** Toggles individual add-in accordions within the Configure section */
    const toggleAddIn = (id) => (_, expanded) => {
        setOpenAddIns((prev) => {
            const next = new Set(prev);
            if (expanded) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    /** Toggles individual configuration accordions within each add-in */
    const toggleConfig = (id) => (_, expanded) => {
        setOpenConfigs((prev) => {
            const next = new Set(prev);
            if (expanded) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const openNewConfigDialog = (addInId) => {
        const addIn = data.addIns.find((a) => a.id === addInId);
        const initialFields = {};

        // Initialize fields — auto-populate read-only key values where defined
        if (addIn?.configurationFields) {
            addIn.configurationFields.forEach((field) => {
                initialFields[field.keyName] = field.readOnly ? (field.defaultValue || "") : "";
            });
        }

        setEditorState({
            addInId,
            configId: null,
            configurationName: "",
            fields: initialFields,
        });
        setEditorOpen(true);
    };

    const openEditConfigDialog = (addInId, config) => {
        setEditorState({
            addInId,
            configId: config.id,
            configurationName: config.configurationName || "",
            fields: { ...config.fields },
        });
        setEditorOpen(true);
    };

    const closeEditorDialog = () => {
        setEditorState({
            addInId: null,
            configId: null,
            configurationName: "",
            fields: {},
        });
        setEditorOpen(false);
    };

    const onEditorChange = (field) => (event) => {
        setEditorState((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const onEditorFieldChange = (fieldName) => (event) => {
        setEditorState((prev) => ({
            ...prev,
            fields: {
                ...prev.fields,
                [fieldName]: event.target.value,
            },
        }));
    };

    const saveConfiguration = async () => {
        const { addInId, configId, configurationName, fields } = editorState;
        const trimmedConfigName = configurationName.trim();
        const { enterpriseId } = queryParams;

        // Validate required fields
        if (!trimmedConfigName) {
            toast.error("Configuration Name is required.");
            return;
        }

        // Validate configuration name length (1–30 chars)
        if (trimmedConfigName.length > 30) {
            toast.error("Configuration Name must not exceed 30 characters.");
            return;
        }

        // Validate uniqueness within the same add-in (skip check when editing the same config)
        const addIn = data.addIns.find((a) => a.id === addInId);
        const isDuplicate = addIn?.configurations.some(
            (cfg) =>
                cfg.configurationName.trim().toLowerCase() === trimmedConfigName.toLowerCase() &&
                cfg.id !== configId,
        );
        if (isDuplicate) {
            toast.error("Configuration Name already exists for this Add-in.");
            return;
        }

        // Validate all required configuration fields
        if (addIn?.configurationFields) {
            for (const field of addIn.configurationFields) {
                const value = fields[field.keyName]?.trim() || "";
                if (!value) {
                    toast.error(`${field.userLabel || field.keyName} is required.`);
                    return;
                }
            }
        }

        // Build configurationValues from template fields + entered values
        const configurationValues = (addIn?.configurationFields || []).map((field) => ({
            keyName: field.keyName,
            userLabel: field.userLabel,
            hasKeyValue: true,
            keyValue: fields[field.keyName] || "",
            addTo: field.addTo ?? 1,
        }));

        try {
            showLoader(true);

            let response;
            if (configId) {
                // Edit existing configuration
                response = await ApiServices.httpPut(
                    ADDIN_CONFIGURATIONEDIT(enterpriseId, addInId, configId),
                    {
                        configurationId: configId,
                        enterpriseGroupId: enterpriseId,
                        addInId,
                        configurationName: trimmedConfigName,
                        configurationValues,
                    }
                );
            } else {
                // Add new configuration
                response = await ApiServices.httpPost(
                    ADDIN_CONFIGURATIONADD(enterpriseId, addInId),
                    {
                        enterpriseGroupId: enterpriseId,
                        addInId,
                        configurationName: trimmedConfigName,
                        configurationValues,
                    }
                );
            }

            if (!response?.responseStatus?.isSuccess) {
                throw new Error(response?.responseStatus?.error || "Failed to save configuration.");
            }

            // Update local state on success
            setData((prev) => ({
                ...prev,
                addIns: upsertAddInConfiguration({
                    addIns: prev.addIns,
                    addInId,
                    configId,
                    configurationName: trimmedConfigName,
                    fields,
                }),
            }));

            setEditorOpen(false);
            toast.success(configId ? "Configuration updated." : "Configuration added.");
        } catch (error) {
            toast.error(error?.message || "Failed to save configuration.");
        } finally {
            showLoader(false);
        }
    };

    const openDeleteConfirmation = (addInId, configId) => {
        setDeleteState({ open: true, addInId, configId });
    };

    const closeDeleteConfirmation = () => {
        setDeleteState({ open: false, addInId: null, configId: null });
    };

    const confirmDelete = async () => {
        const { addInId, configId } = deleteState;
        const { enterpriseId } = queryParams;

        try {
            showLoader(true);

            const response = await ApiServices.httpDelete(
                ADDIN_CONFIGURATIONDELETE(enterpriseId, addInId, configId)
            );

            if (!response?.responseStatus?.isSuccess) {
                throw new Error(response?.responseStatus?.error || "Failed to delete configuration.");
            }

            // Remove from local state on success
            setData((prev) => ({
                ...prev,
                addIns: removeAddInConfiguration({
                    addIns: prev.addIns,
                    addInId,
                    configId,
                }),
            }));

            closeDeleteConfirmation();
            toast.success("Configuration deleted.");
        } catch (error) {
            toast.error(error?.message || "Failed to delete configuration.");
        } finally {
            showLoader(false);
        }
    };

    /**
     * Handles subscriber assignment mode change
     */
    const onSubscriberAssignmentChange = (event) => {
        setSubscriberAssignment(event.target.value);
        page.setDirty(true);
    };

    /**
     * Callback when selected subscribers change in AddInsManageSubscriber component
     */
    const handleSelectedSubscribersChange = (subscriberIds = []) => {
        setSelectedSubscriberIds(
            subscriberIds
                .map((subscriberId) => Number(subscriberId))
                .filter((subscriberId) => subscriberId > 0),
        );
        page.setDirty(true);
    };

    const onCancel = () => {
        history.goBack();
    };

    /** Saves the selected subscriber assignment mode for this add-in. */
    const saveSubscriberAssignmentMode = async () => {
        const { enterpriseId, addInsId } = queryParams;
        const assignmentMode = subscriberAssignment === "selected" ? 2 : 1;
        const payload = { assignmentMode };
        let shouldReloadSubscriberAssignment = false;

        showLoader(true);
        try {
            if (assignmentMode === 2) {
                const subscriberResponse = await ApiServices.httpPost(
                    ADDIN_CONFIG_ALL_SUBS(enterpriseId, addInsId),
                    buildSubscriberAssignmentPayload(enterpriseId, addInsId),
                );

                if (!subscriberResponse?.responseStatus?.isSuccess) {
                    throw new Error(
                        subscriberResponse?.responseStatus?.error || "Failed to load subscribers.",
                    );
                }

                const subscriberAccess = getFirstSubscriberAccess(subscriberResponse) ||
                    (selectedSubscriberIds[0]
                        ? {
                            subscriberId: selectedSubscriberIds[0],
                            hasAccess: true,
                        }
                        : null);

                if (!subscriberAccess?.subscriberId) {
                    toast.error("At least one subscriber must be added in Selected Subscribers mode.");
                    return false;
                }

                payload.subscriberAccess = [subscriberAccess];
                shouldReloadSubscriberAssignment = subscriberAccess.hasAccess === false;
            }

            const response = await ApiServices.httpPut(
                ADDIN_CONFIG_ALL_SUBSUPDATE(enterpriseId, addInsId),
                payload,
            );
            if (!response?.responseStatus?.isSuccess) {
                throw new Error(
                    response?.responseStatus?.error || "Failed to associate subscribers.",
                );
            }

            if (shouldReloadSubscriberAssignment) {
                await loadSubscriberAssignment();
            }

            if (assignmentMode === 2) {
                setSubscriberTableRefreshKey((prev) => prev + 1);
            }

            return true;
        } catch (error) {
            toast.error(error?.message || "Failed to associate subscribers.");
            return false;
        } finally {
            showLoader(false);
        }
    };

    const onUpdateDetails = async () => {
        const { enterpriseId, addInsId } = queryParams;
        if (!enterpriseId || !addInsId) {
            toast.error("Missing enterprise or add-in ID.");
            return;
        }

        const ok = await saveSubscriberAssignmentMode();
        if (!ok) return;

        page.setDirty(false);
        toast.success(
            subscriberAssignment === "all"
                ? "All subscribers have been associated with this Add-in."
                : "Subscriber assignment updated successfully.",
            { toastId: "addins-config-update" },
        );
    };

    const onUpdateNext = async () => {
        const { enterpriseId, addInsId } = queryParams;
        if (!enterpriseId || !addInsId) {
            toast.error("Missing enterprise or add-in ID.");
            return;
        }

        if (openSection === "subscribers") {
            const ok = await saveSubscriberAssignmentMode();
            if (!ok) return;
        }

        page.setDirty(false);
        const currentIdx = SECTION_ORDER.indexOf(openSection);
        const nextSection =
            currentIdx >= 0 && currentIdx < SECTION_ORDER.length - 1
                ? SECTION_ORDER[currentIdx + 1]
                : "";
        setOpenSection(nextSection);
        toast.success("Configuration saved.", { toastId: "addins-config-next" });
    };

    /** Returns true when none of the add-ins require configuration */
    const noneRequireConfiguration = data.addIns.length > 0 && data.addIns.every((a) => !a.requiresConfiguration);

    /** Returns true when at least one add-in requires configuration but has none saved yet */
    const hasUnconfiguredAddIns = data.addIns.some(
        (a) => a.requiresConfiguration && a.configurations.length === 0,
    );

    /** Navigates to the next accordion section without saving */
    const onNext = () => {
        const currentIdx = SECTION_ORDER.indexOf(openSection);
        const nextSection =
            currentIdx >= 0 && currentIdx < SECTION_ORDER.length - 1
                ? SECTION_ORDER[currentIdx + 1]
                : "";
        setOpenSection(nextSection);
    };

    /** Returns true when the subscription is inactive/expired and changes must be blocked */
    const isSubscriptionInactive = () => {
        const status = (data.pageSummary.subscriptionStatus || "").toLowerCase();
        return status === "inactive" || status === "expired";
    };

    const getStatusLabel = (addIn) => {
        if (!addIn.requiresConfiguration) return "Configured";
        return addIn.configurations.length ? "Configured" : "Not Configured";
    };

    const getStatusClassName = (addIn) =>
        getStatusLabel(addIn) === "Configured"
            ? "aisc-status aisc-status--ok"
            : "aisc-status aisc-status--warn";

    return (
        <Box className="aisc-page">
            <Box className="aisc-header">
                <Breadcrumbs separator="›" aria-label="breadcrumb" className="aisc-breadcrumbs distributor-breadcrumbs">
                    <Link href="/enterprisegroups" underline="hover" className="breadcrumb-title">
                        Enterprise Group Listing
                    </Link>
                    <Link href={`/enterprisegroup/${queryParams?.enterpriseId}/add-ins`} underline="hover" className="breadcrumb-title">
                        Add-ins
                    </Link>
                    <Typography className="content-last">Manage Add-ins</Typography>
                </Breadcrumbs>
            </Box>

            <Grid container spacing={2} className="aisc-summary-grid">
                {summaryCards.map((item) => (
                    <Grid item xs={12} sm={6} md={2} key={item.label} mb={2}>
                        <Box className="aisc-summary-card">
                            <Typography className="aisc-summary-label">{item.label}</Typography>
                            <Typography className="aisc-summary-value">{item.value}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <Accordion
                expanded={openSection === "configure"}
                onChange={toggleSection("configure")}
                className="aisc-section"
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className="aisc-section-title">Configure Add-ins</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {data.addIns.map((addIn) => (
                        <Accordion
                            key={addIn.id}
                            expanded={openAddIns.has(addIn.id)}
                            onChange={toggleAddIn(addIn.id)}
                            className="aisc-inner-section"
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography className="aisc-inner-title">
                                    {addIn.integrationSolutionName || addIn.name}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box className="aisc-addin-block">
                                    <Box className="aisc-addin-head">
                                        <Typography className="aisc-addin-name">{addIn.integrationSolutionName || addIn.name}</Typography>
                                        <Box className={getStatusClassName(addIn)} sx={{ fontSize: '0.85rem', py: 0.5, px: 1 }}>
                                            {getStatusLabel(addIn)}
                                        </Box>
                                    </Box>

                                    {isSubscriptionInactive() && (
                                        <Box sx={{ p: 2, mb: 1, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107', color: '#856404' }}>
                                            Configuration cannot be modified because the subscription is inactive.
                                        </Box>
                                    )}

                                    {addIn.requiresConfiguration ? (
                                        <Box>
                                            {addIn.configurations.length === 0 && (
                                                <Box sx={{ p: 2, color: 'text.secondary' }}>
                                                    No configuration found.
                                                </Box>
                                            )}
                                            {addIn.configurations.map((cfg) => (
                                                <Accordion
                                                    key={cfg.id}
                                                    expanded={openConfigs.has(cfg.id)}
                                                    onChange={toggleConfig(cfg.id)}
                                                    sx={{
                                                        mb: 1,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: '4px !important',
                                                        boxShadow: 'none',
                                                        '&:before': { display: 'none' },
                                                    }}
                                                >
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Typography sx={{ fontWeight: 500 }}>
                                                            {cfg.configurationName}
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ p: 0 }}>
                                                        <Box className="aisc-table-wrap">
                                                            <table className="aisc-table">
                                                                <thead>
                                                                    <tr>
                                                                        {addIn.configurationFields.map((field) => (
                                                                            <th key={field.keyName}>{field.userLabel || field.keyName}</th>
                                                                        ))}
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr>
                                                                        {addIn.configurationFields.map((field) => (
                                                                            <td key={field.keyName}>
                                                                                {field.keyName.toLowerCase().includes('password')
                                                                                    ? MASKED_VALUE
                                                                                    : (cfg.fields[field.keyName] || '-')}
                                                                            </td>
                                                                        ))}
                                                                        <td>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => openEditConfigDialog(addIn.id, cfg)}
                                                                                aria-label="edit-configuration"
                                                                                disabled={isSubscriptionInactive()}
                                                                            >
                                                                                <EditOutlinedIcon fontSize="small" />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => openDeleteConfirmation(addIn.id, cfg.id)}
                                                                                aria-label="delete-configuration"
                                                                                disabled={isSubscriptionInactive()}
                                                                            >
                                                                                <DeleteOutlineOutlinedIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </Box>
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box className="aisc-info-box">{addIn.noConfigMessage}</Box>
                                    )}

                                    {addIn.requiresConfiguration && !isSubscriptionInactive() && (addIn.allowMultipleConfigurations || addIn.configurations.length === 0) && (
                                        <Button
                                            variant="outlined"
                                            className="aisc-add-config-btn"
                                            startIcon={<AddOutlinedIcon />}
                                            onClick={() => openNewConfigDialog(addIn.id)}
                                        >
                                            Add Configuration
                                        </Button>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))
                    }

                    <Box className="aisc-footer-wrap">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                            {noneRequireConfiguration ? (
                                <Button
                                    variant="contained"
                                    aria-label="Next"
                                    onClick={onNext}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    aria-label="Update Next"
                                    onClick={onUpdateNext}
                                    disabled={hasUnconfiguredAddIns}
                                >
                                    Update & Next
                                </Button>
                            )}
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion
                expanded={openSection === "subscribers"}
                onChange={toggleSection("subscribers")}
                className="aisc-section"
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className="aisc-section-title">Associate Subscribers</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className="aisc-associate-body">
                        <RadioGroup
                            value={subscriberAssignment}
                            onChange={onSubscriberAssignmentChange}
                            name="associate-subscribers"
                        >
                            <Box className="aisc-assignment-option">
                                <FormControlLabel
                                    value="all"
                                    control={<Radio size="small" />}
                                    label="Assign to All Subscribers"
                                />
                                <Typography className="aisc-assignment-helptext">
                                    All current and future subscribers will have access to this Add-in.
                                </Typography>
                            </Box>

                            <Box className="aisc-assignment-option">
                                <FormControlLabel
                                    value="selected"
                                    control={<Radio size="small" />}
                                    label="Assign to Selected Subscribers"
                                />
                            </Box>
                        </RadioGroup>

                        {subscriberAssignment === "selected" && (
                            isSubscriberAssignmentLoaded && (
                                <AddInsManageSubscriber
                                    enterpriseId={queryParams.enterpriseId}
                                    addInId={queryParams.addInsId}
                                    onSubscribersChange={handleSelectedSubscribersChange}
                                    assignmentMode={2}
                                    refreshKey={subscriberTableRefreshKey}
                                />
                            )
                        )}

                        <Box className="aisc-footer-wrap">
                            <Box
                                data-testid="footer-buttons"
                                sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={onUpdateDetails}
                                >
                                    Update
                                </Button>
                                <Button
                                    variant="contained"
                                    aria-label="Update Next"
                                    onClick={onUpdateNext}
                                >
                                    Update & Next
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion
                expanded={openSection === "subscription"}
                onChange={toggleSection("subscription")}
                className="aisc-section"
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className="aisc-section-title">Subscription Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                       <ManageSubscriptiondetails 
                            enterpriseGroupId={queryParams.enterpriseId}
                            addInId={queryParams.addInsId}
                       />
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Dialog open={editorOpen} style={{ padding: "20px" }} onClose={closeEditorDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {editorState.configId ? "Edit Configuration" : "Add Configuration"}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
                        Configure credentials for {data.addIns.find(a => a.id === editorState.addInId)?.integrationSolutionName || "Add-in"}
                    </Typography>
                </DialogTitle>
                <DialogContent style={{ padding: "20px" }}>


                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                            Configuration Name <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter configuration name"
                            value={editorState.configurationName}
                            onChange={onEditorChange("configurationName")}
                            variant="outlined"
                            size="small"
                        />
                    </Box>

                    {data.addIns.find(a => a.id === editorState.addInId)?.configurationFields?.map((field) => (
                        <Box key={field.keyName} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                {field.userLabel || field.keyName} <span style={{ color: "red" }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                type={field.keyName.toLowerCase().includes('password') ? 'password' : 'text'}
                                placeholder={`Enter ${(field.userLabel || field.keyName).toLowerCase()}`}
                                value={editorState.fields[field.keyName] || ""}
                                onChange={onEditorFieldChange(field.keyName)}
                                variant="outlined"
                                size="small"
                                inputProps={{ readOnly: !!field.readOnly }}
                            />
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions style={{ padding: "20px" }}>
                    <Button onClick={closeEditorDialog} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={saveConfiguration} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Confirmation
                open={deleteState.open}
                handleClose={closeDeleteConfirmation}
                title="Delete Configuration"
                description="Are you sure you want to delete this configuration?"
                onCancel={closeDeleteConfirmation}
                onConfirm={confirmDelete}
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Delete"
            />
        </Box>
    );
};

export default AddInsServiceConfiguration;
